'use client'

import { CollectionTemplateDefinitionCollection, CollectionTemplateDefinitionDatabase, UUID } from '@indigohive/cogfy-types'
import { CollectionTemplateTable } from './__components/CollectionTemplateTable'
import { CollectionTemplateTree } from './__components'
import { useState } from 'react'
import { TemplateItem } from './template-item'

export function analyzeCollectionTemplate (collection: CollectionTemplateDefinitionCollection) {
  let databaseCount = 0
  let folderCount = 0
  const folderIds: UUID[] = []
  let firstDatabase: CollectionTemplateDefinitionDatabase | null = null

  function recursive (collection: CollectionTemplateDefinitionCollection): TemplateItem {
    if (collection.type === 'database') {
      databaseCount++

      if (!firstDatabase) {
        firstDatabase = collection
      }
    } else if (collection.type === 'folder') {
      folderCount++
      folderIds.push(collection.id)
    }

    return {
      id: collection.id,
      name: collection.name,
      type: collection.type,
      fields: collection.type === 'database' ? collection.fields : undefined,
      views: collection.type === 'database' ? collection.views : undefined,
      children: collection.type === 'folder' ? collection.children.map(recursive) : undefined
    }
  }

  const rootItem = recursive(collection)

  return {
    rootItem,
    summary: {
      databases: databaseCount,
      folders: folderCount,
      folderIds,
      firstDatabase
    }
  }
}

export type TemplatePreviewProps = {
  collection: CollectionTemplateDefinitionCollection
}

export function TemplatePreview ({ collection }: TemplatePreviewProps) {
  const { rootItem, summary } = analyzeCollectionTemplate(collection)
  const { databases, folders, folderIds, firstDatabase } = summary

  const [foldersState, setFoldersState] = useState<Record<string, boolean>>(
    folderIds.reduce<Record<string, true>>((acc, id) => {
      acc[id] = true
      return acc
    }, {})
  )
  const [selectedTable, setSelectedTable] = useState<TemplateItem | null>(firstDatabase)

  return (
    <div id="template-preview">
      <div className='flex flex-col md:flex-row gap-2 items-center text-base mb-3 manrope-bold'>
        <span className="text-lg">In this template:</span>
        <div className='flex gap-2'>
          {databases !== 0 && (
            <div className="text-base px-2 py-1 rounded-lg bg-primary text-primary-content manrope-bold">
              {databases} {databases > 1 ? 'Collections' : 'Collection'}
            </div>
          )}
          {folders !== 0 && (
            <div className="text-base px-2 py-1 rounded-lg bg-primary text-primary-content manrope-bold">
              {folders} {folders > 1 ? 'Folders' : 'Folder'}
            </div>
          )}
        </div>
      </div>

      <div className='grid grid-cols-12 grid-rows-1 gap-3 w-full'>
        <CollectionTemplateTree
          rootItem
          item={rootItem}
          foldersState={foldersState}
          setFoldersState={setFoldersState}
          selectedTable={selectedTable}
          setSelectedTable={setSelectedTable}
          className="col-span-12 md:col-span-4"
        />

        <CollectionTemplateTable
          table={selectedTable}
          className="col-span-12 md:col-span-8"
        />
      </div>
    </div>
  )
}
